// @givmo/app-admin — onboarding wizard step 3 (optional custom message + required-language preview).
//
// Ports the Flutter `OnboardingStepThree`: a checkbox + a multiline message textarea enabled ONLY
// when checked, then a read-only preview of the Givmo-required language Givmo appends below the
// custom message. The message field is the SaaS `<Textarea>` (same tokens as `<Field>`). Verbatim copy.

import { Checkbox, Textarea } from '@givmo/design-system';
import { GIVMO_MATCH_CRITERIA } from './onboarding-consts';

export interface OnboardingStepThreeProps {
  isChecked: boolean;
  value: string;
  onChange: (next: { isChecked: boolean; value: string }) => void;
}

const HINT =
  'i.e. We will generally approve match requests to charities that support education, health, science, and disaster relief.';

export function OnboardingStepThree({ isChecked, value, onChange }: OnboardingStepThreeProps) {
  return (
    <div className="flex flex-col items-stretch" data-testid="onboarding-step-3">
      <h2 className="font-sans text-s20 font-w700 tracking-givmo text-text-strong">
        Add a custom message (optional)
      </h2>
      <p className="mt-8 font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
        Tell employees what kinds of charities your company is willing to match. This appears at the
        top of your Match Policy.
      </p>

      <div className="mt-24 flex items-center gap-12" data-testid="onboarding-custom-toggle">
        <Checkbox
          appearance="saas"
          checked={isChecked}
          onCheckedChange={(checked) => onChange({ isChecked: checked, value })}
          aria-label="Include a custom message"
          testId="onboarding-custom-checkbox"
        />
        <span className="font-sans text-s15 font-w500 tracking-normal text-text">
          Include a custom message
        </span>
      </div>

      <Textarea
        value={value}
        disabled={!isChecked}
        rows={4}
        placeholder={HINT}
        aria-label="Custom match-policy message"
        onChange={(event) => onChange({ isChecked, value: event.target.value })}
        containerClassName="mt-8"
        testId="onboarding-custom-message"
      />

      <p className="mt-24 font-sans text-s14 font-w400 tracking-normal text-text-secondary">
        Givmo will append this required language below your message:
      </p>
      <div
        className="mt-8 rounded-md border border-border-subtle bg-surface-sunken px-12 py-12 font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
        data-testid="onboarding-required-language"
      >
        {GIVMO_MATCH_CRITERIA}
      </div>
    </div>
  );
}
